import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";

const EXAMPLES = [
  "I'm worried about money and don't know what to do next…",
  "My relationship with my daughter is strained…",
  "I'm overwhelmed and having trouble making a decision…",
];

export default function PersonalGuidance() {
  const nav = useNavigate();
  const [text, setText] = useState(() => sessionStorage.getItem("pg_situation") || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  useEffect(() => { sessionStorage.setItem("pg_situation", text); }, [text]);

  const submit = async () => {
    const situation = text.trim();
    if (situation.length < 4) { toast.error("Please tell us a little about what you're facing."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wisdom-path", { situation, source: "wisdom" });
      if (!data?.verses?.length) { toast.error("We couldn't find a match. Try describing it a little differently."); return; }
      nav("/wisdom-path", { state: { result: data, query: { issues: data.issues || [], situation, source: "wisdom" } } });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12">
      <button data-testid="guidance-back" onClick={() => nav("/")} className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#2C4C3B] mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-3">Get personal guidance</p>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A] leading-tight">What's happening?</h1>
      <p className="mt-4 text-[#475569] leading-relaxed">
        Tell us what you're facing. You can write as much or as little as you'd like, and we'll help you find Scripture that speaks to your situation.
      </p>

      <textarea
        data-testid="guidance-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder={EXAMPLES[0]}
        className="mt-6 w-full rounded-2xl border border-[#E2E8F0] bg-white px-4 py-4 text-base text-[#0F172A] leading-relaxed focus:border-[#2C4C3B] outline-none resize-y"
      />

      <div className="mt-3 text-xs text-[#94A3B8]">
        <span className="font-semibold text-[#B8912F]">For example:</span>
        <ul className="mt-1 space-y-0.5">
          {EXAMPLES.map((ex) => <li key={ex} className="italic">“{ex}”</li>)}
        </ul>
      </div>

      <button
        data-testid="guidance-submit"
        onClick={submit}
        disabled={busy}
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#2C4C3B] text-white px-7 py-3.5 font-semibold hover:bg-[#24402F] transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        Find Scripture for My Situation
      </button>

      <p className="mt-6 text-sm text-[#94A3B8]">
        Prefer to choose from a list instead?{" "}
        <button onClick={() => nav("/")} className="text-[#2C4C3B] font-semibold underline">Browse by Topic</button>
      </p>
    </div>
  );
}
